import Service from '@ember/service';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import ApiService, { ApiError } from 'cio/services/api';
import ApiCredentialModel from 'cio/models/api-credential';
import ApiErrorsService from 'cio/services/api-errors';
import AlertsService from 'cio/services/alerts';
import OAuthService from './oauth';
import { Provider } from 'cio/utils/oauth';

export enum SegmentIntegration {
  Source = 'segdotcom',
  Destination = 'segment_in',
}

export enum SegmentAuthAction {
  Default = '',
  Create = 'create',
  Enable = 'enable',
  Check = 'check',
  Delete = 'delete',
  Disable = 'disable',
}

export enum SegmentAuthResult {
  Created = 'created',
  Enabled = 'enabled',
  Deleted = 'deleted',
  Disabled = 'disabled',
  Errored = 'errored',
}

export type SegmentResponse = {
  enabled?: boolean;
};
export type SegmentValidationError = {
  errors: {
    [key: string]: any;
    detail: string;
    status: string;
    meta: Record<string, string>;
  }[];
};
export type SegmentOAuthResponse = null | SegmentResponse | SegmentValidationError;
export type SegmentAuthenticateResponse = Partial<SegmentValidationError> & {
  state: SegmentAuthResult;
  userAction: SegmentAuthAction;
  enabled?: SegmentResponse['enabled'];
};

export default class SegmentAuthService extends Service {
  @service alerts!: AlertsService;
  @service api!: ApiService;
  @service apiErrors!: ApiErrorsService;
  @service oauth!: OAuthService;

  mapActionToResult(action: SegmentAuthAction): SegmentAuthResult {
    switch (action) {
      case SegmentAuthAction.Delete:
        return SegmentAuthResult.Deleted;
      case SegmentAuthAction.Disable:
        return SegmentAuthResult.Disabled;
      case SegmentAuthAction.Enable:
        return SegmentAuthResult.Enabled;
      default:
        return SegmentAuthResult.Created;
    }
  }

  segmentIn = task({ keepLatest: true }, async () => {
    try {
      let response = await this.api.request('integrations/segment_in', {
        ignoreErrors: true,
      });
      return response;
    } catch {
      return false;
    }
  });

  deleteConnection = task(async (integration: SegmentIntegration) => {
    try {
      let response = await this.api.delete(`integrations/${integration}`, {
        ignoreErrors: true,
      });

      this.alerts.success(`integration.${integration}.delete.success`, 'Connection cleared');

      return response;
    } catch {
      this.alerts.error(
        `integration.${integration}.delete.error`,
        'Something went wrong when trying to clear your connection. Please try again.',
        'Uh oh!',
      );
    }
  });

  authenticate = task(
    async (selectedCredential: ApiCredentialModel, action: SegmentAuthAction = SegmentAuthAction.Default) => {
      // Clear any alerts from previous attempts
      this.alerts.clearAlerts();
      // 1. Main OAuth authorization, obtain OAuth auth code
      let response;
      try {
        response = await this.oauth.open(Provider.Segment, { width: 640 });
      } catch (e) {
        this.alerts.error(
          'integration.segment_in.error',
          "We weren't able to access your Segment workspace. Please try again.",
        );

        // Throw the error again so it bubbles up to the parent callers running this task.
        throw e;
      }

      // 2. Pass the received OAuth key to the back-end, which has client secret and can exchange it for Segment install token
      // then use it to set-up Customer.io destination in customer's Segment workspace - using selected API credentials
      let data = {
        code: response.authorizationCode,
        redirect_uri: response.redirectUri,
        site_id: selectedCredential.siteId,
        api_key: selectedCredential.apiKey,
        action,
      };

      try {
        let oauthResponse: SegmentOAuthResponse = await this.api.post<SegmentOAuthResponse>('oauth2/segment', {
          data,
          ignoreErrors: true,
        });

        if (!oauthResponse) {
          return {
            state: this.mapActionToResult(action),
            userAction: action,
          };
        } else if ('enabled' in oauthResponse && oauthResponse.enabled) {
          return {
            state: this.mapActionToResult(action),
            enabled: oauthResponse?.enabled,
            userAction: action,
          };
        } else if ('enabled' in oauthResponse && oauthResponse.enabled === false) {
          return {
            state: SegmentAuthResult.Errored,
            enabled: oauthResponse?.enabled,
            userAction: action,
          };
        } else if ('errors' in oauthResponse) {
          return {
            state: SegmentAuthResult.Errored,
            errors: oauthResponse.errors,
            userAction: action,
          };
        }

        return {
          state: this.mapActionToResult(action),
        };
      } catch (errorResponse) {
        // handle "destination already exists" error separately: Segment API error bubbling up through Customer.io API response
        if ((errorResponse.errors || []).some((err: ApiError) => err.detail === 'destination already exists')) {
          this.alerts.warning(
            'integration.segment_in.destination_already_exists',
            'Customer.io destination already exists in your selected Segment workspace.',
          );
          // Let the caller handle these errors and don't snitch to the apiErrors service
        } else if (
          (errorResponse.errors || []).some((err: ApiError) =>
            ['destination not found', 'source mismatch', 'destination mismatch'].includes(err.detail),
          )
        ) {
          throw {
            state: SegmentAuthResult.Errored,
            userAction: action,
            errors: errorResponse.errors,
          };
          // Let the caller handle the not found error and don't snitch to the apiErrors service
        } else if (
          typeof errorResponse !== 'string' &&
          'errors' in errorResponse &&
          errorResponse.errors[0]?.status === '404'
        ) {
          throw {
            state: SegmentAuthResult.Errored,
            userAction: action,
            errors: errorResponse.errors,
          };
        } else {
          this.apiErrors.report(errorResponse);
        }

        // Throw the error again so it bubbles up to the parent callers running this task.
        throw errorResponse;
      }
    },
  );
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    'segment-auth': SegmentAuthService;
  }
}
